import { Popconfirm } from "antd";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";
import { formatJsDate } from "../../utils/formatageDate";
import { AddDocumentModal } from "../../components/reusableComponents/AddDocumentModal";
import MyPDFViewer from "../../components/reusableComponents/PDFViewer";
import {
  Loader2,
  FileText,
  Download,
  Eye,
  Trash2,
  Plus,
  Upload,
  Shield,
  GraduationCap,
  Building,
  Award,
  File,
} from "lucide-react";

const DocumentSection = ({ candidate }) => {
  const [documents, setDocuments] = useState([]);
  const [category, setCategory] = useState("all");
  const categories = [
    { id: "payslips", label: "Fiches de paie", matches: type => type.startsWith("fiche de paie") || type === "bulletin de paie" },
    { id: "france", label: "Attestation France Travail", matches: type => ["attestation france travail", "attestation pôle emploi", "attestation pole emploi"].includes(type) },
    { id: "work", label: "Certificat de travail", matches: type => type === "certificat de travail" },
    { id: "tax", label: "Attestation fiscale", matches: type => type === "attestation fiscale" },
  ];
  const inCategory = (doc, group) => group.matches(String(doc.type || "").trim().toLowerCase());
  const visibleDocuments = category === "all" ? documents : documents.filter(doc => inCategory(doc, categories.find(group => group.id === category)));
  const [viewingId, setViewingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [showDocument, setShowDocument] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [cvBusy, setCvBusy] = useState(false);
  const openCv = async () => {
    if (cvBusy) return;
    setCvBusy(true);
    try {
      const {data} = await axios.get(`cv/${candidate._id}`);
      if (!data?.data) throw new Error("CV absent");
      setPreviewUrl(data.data.replace(/^data:[^,]+,/, ""));
      setDocumentType("pdf");
      setShowDocument(true);
    } catch { toast.error("CV indisponible pour ce candidat."); }
    finally {setCvBusy(false);}
  };

  useEffect(() => {
    loadDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      console.log("Chargement des documents pour le candidat:", candidate._id);
      const res = await axios.get(`/document/candidate/${candidate._id}`);
      console.log("Documents reçus:", res.data);

      setDocuments(
        res.data.map((document, idx) => ({
          ...document,
          key: idx,
        })),
      );

      if (res.data.length === 0) {
        console.log("Aucun document trouvé pour ce candidat");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des documents:", error);

      if (error.response?.status === 404) {
        console.log("Aucun document trouvé pour ce candidat");
        setDocuments([]);
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé aux documents");
        setDocuments([]);
      } else {
        toast.error("Erreur lors du chargement des documents");
        setDocuments([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getDocument = async (documentId) => {
    if (viewingId) return;
    setViewingId(documentId);
    try {
      console.log("Récupération du document:", documentId);

      const res = await axios.get(`/document/${documentId}`);
      console.log("Document reçu:", res.data);

      const { name, data } = res.data;
      const mimeType = name.split(".").pop().toLowerCase();

      setPreviewUrl(data);
      setDocumentType(mimeType);
      setShowDocument(true);

    } catch (error) {
      console.error("Erreur lors de la récupération du document:", error);

      if (error.response?.status === 404) {
        toast.error("Document non trouvé");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé à ce document");
      } else {
        toast.error("Erreur lors du chargement du document");
      }
    } finally { setViewingId(null); }
  };

  const downloadDocument = async (documentId, name) => {
    try {
      console.log("Téléchargement du document:", documentId, name);
      toast.info("Préparation du téléchargement...");

      const res = await axios.get(`/document/${documentId}`);
      const { data } = res.data;

      // Créer le blob et le lien de téléchargement
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Déterminer le type MIME basé sur l'extension
      const extension = name.split('.').pop().toLowerCase();
      let mimeType = 'application/octet-stream';

      switch (extension) {
        case 'pdf':
          mimeType = 'application/pdf';
          break;
        case 'jpg':
        case 'jpeg':
          mimeType = 'image/jpeg';
          break;
        case 'png':
          mimeType = 'image/png';
          break;
        case 'doc':
          mimeType = 'application/msword';
          break;
        case 'docx':
          mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          break;
        default:
          mimeType = 'application/octet-stream';
      }

      const blob = new Blob([byteArray], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${name} téléchargé avec succès`);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);

      if (error.response?.status === 404) {
        toast.error("Document non trouvé");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé à ce document");
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      console.log("Suppression du document:", documentId);
      await axios.delete(`/document/${documentId}`);
      toast.success("Document supprimé avec succès");
      loadDocuments(); // Recharger la liste
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);

      if (error.response?.status === 404) {
        toast.error("Document non trouvé");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé pour supprimer ce document");
      } else {
        toast.error("Erreur lors de la suppression du document");
      }
    }
  };

  // Types identiques au dashboard jminterim (documentsList)
  const identityTypes = [
    "carte d'identité",
    "passeport",
    "permis de conduire",
    "carte de séjour",
    "carte de résident",
    "carte européenne",
    "autorisation provisoire de séjour",
    "carte vitale",
  ];

  const formationTypes = ["diplôme", "caces", "habilitation", "fco", "fimo"];

  const contractTypes = ["contrat", "devis signé", "devis non signé", "kbis"];

  const attestationTypes = [
    "certificat de travail",
    "attestation pôle emploi",
    "attestation france travail",
    "attestation fiscale",
    "visite médicale",
    "vaccination",
  ];

  const getDocumentIcon = (type) => {
    const t = type?.toLowerCase();
    if (identityTypes.includes(t))
      return <Shield className="w-5 h-5 text-green-600" />;
    if (formationTypes.includes(t))
      return <GraduationCap className="w-5 h-5 text-purple-600" />;
    if (contractTypes.includes(t))
      return <Building className="w-5 h-5 text-orange-600" />;
    if (attestationTypes.includes(t))
      return <Award className="w-5 h-5 text-yellow-600" />;
    return <File className="w-5 h-5 text-gray-600" />;
  };

  const getDocumentTypeColor = (type) => {
    const t = type?.toLowerCase();
    if (identityTypes.includes(t))
      return "bg-green-100 text-green-800 border-green-200";
    if (formationTypes.includes(t))
      return "bg-purple-100 text-purple-800 border-purple-200";
    if (contractTypes.includes(t))
      return "bg-orange-100 text-orange-800 border-orange-200";
    if (attestationTypes.includes(t))
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isDocumentExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const isDocumentExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  return (
    <div className="space-y-6">
      {showDocument && (
        <>
          {documentType === "pdf" ? (
            <MyPDFViewer
              base64={previewUrl}
              isOpen={showDocument}
              onClose={setShowDocument}
            />
          ) : (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white p-4 rounded shadow-lg relative">
                <button
                  onClick={() => setShowDocument(false)}
                  className="absolute top-2 right-2 text-gray-600 hover:text-red-600"
                >
                  ✕
                </button>
                <img
                  src={`data:image/${documentType};base64,${previewUrl}`}
                  alt="document"
                  className="max-h-[80vh] max-w-[90vw]"
                />
              </div>
            </div>
          )}
        </>
      )}

      <AddDocumentModal
        candidate={candidate}
        isOpen={isAddDocumentModalOpen}
        documents={documents}
        setDocuments={setDocuments}
        onClose={() => setIsAddDocumentModalOpen(false)}
      />

      {/* Header avec statistiques */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
          <p className="text-gray-600 mt-1">
            Gestion des documents et pièces justificatives
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{documents.length}</div>
            <div className="text-sm text-gray-500">Documents</div>
          </div>
          <motion.button
            onClick={() => setIsAddDocumentModalOpen(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un document</span>
          </motion.button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-3"><FileText className="h-5 w-5 text-blue-800" /><div><h3 className="text-sm font-semibold text-slate-900">CV du candidat</h3><p className="mt-1 text-xs text-slate-500">Le même CV que dans la liste des candidats.</p></div></div>
        <button type="button" disabled={cvBusy} onClick={openCv} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-blue-800 hover:bg-blue-50 disabled:opacity-50">{cvBusy ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}Voir le CV</button>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Catégories de documents">
        {[{ id: "all", label: "Tous les documents" }, ...categories].map(group => <button key={group.id} type="button" aria-pressed={category === group.id} onClick={() => setCategory(group.id)} className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${category === group.id ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{group.label} <span className="ml-2 text-xs">({group.id === "all" ? documents.length : documents.filter(doc => inCategory(doc, group)).length})</span></button>)}
      </div>
      {/* Liste des documents */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Chargement des documents...</p>
          </div>
        </div>
      ) : visibleDocuments.length > 0 ? (
        <div className="app-table-container"><table className="app-table" aria-label="Documents du candidat">
          <thead><tr>{["Type", "Ajouté le", "Expiration", "Actions"].map(label => <th key={label}>{label}</th>)}</tr></thead>
          <tbody>{visibleDocuments.map(doc => <tr key={doc._id}>

            <td><span className={`inline-block rounded-full border px-2 py-1 text-xs ${getDocumentTypeColor(doc.type)}`}>{doc.type}</span>{doc.subType && <p className="mt-1 text-xs text-slate-500">{doc.subType}</p>}</td>
            <td>{doc.createdAt ? formatJsDate(doc.createdAt) : "—"}{doc.deliveryDate && <p className="text-xs text-slate-500">Délivré le {formatJsDate(doc.deliveryDate)}</p>}</td>
            <td className="text-center"><span className={isDocumentExpired(doc.expirationDate) ? "text-red-700" : isDocumentExpiringSoon(doc.expirationDate) ? "text-amber-700" : "text-slate-700"}>{doc.expirationDate ? formatJsDate(doc.expirationDate) : "—"}</span>{isDocumentExpired(doc.expirationDate) && <p className="text-xs text-red-700">Expiré</p>}{!isDocumentExpired(doc.expirationDate) && isDocumentExpiringSoon(doc.expirationDate) && <p className="text-xs text-amber-700">Expire bientôt</p>}</td>
            <td><div className="flex flex-wrap items-center justify-center gap-2">
              <button type="button" disabled={Boolean(viewingId)} aria-busy={viewingId === doc._id} aria-label={`Voir ${doc.name}`} onClick={() => getDocument(doc._id)} className="inline-flex items-center gap-1 rounded-lg p-2 text-blue-800 hover:bg-blue-50">{viewingId === doc._id ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}Voir</button>
              <button type="button" aria-label={`Télécharger ${doc.name}`} onClick={() => downloadDocument(doc._id,doc.name)} className="rounded-lg p-2 text-emerald-700 hover:bg-emerald-50" title="Télécharger"><Download size={16} /></button>
              <Popconfirm title="Supprimer ce document ?" description="Cette suppression est définitive." onConfirm={() => deleteDocument(doc._id)} okText="Supprimer" cancelText="Annuler" okButtonProps={{danger:true}} placement="topRight"><button type="button" aria-label={`Supprimer ${doc.name}`} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="Supprimer"><Trash2 size={16} /></button></Popconfirm>
            </div></td>
          </tr>)}</tbody>
        </table></div>
      ) : (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {category === "all" ? "Aucun document" : `Aucun document : ${categories.find(group => group.id === category)?.label}`}
          </h3>
          <p className="text-gray-600 mb-4">
            Les documents déposés par l’agence seront consultables et téléchargeables ici.
          </p>
          <motion.button
            onClick={() => setIsAddDocumentModalOpen(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            Ajouter le premier document
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default DocumentSection;
